import React, { useState, useEffect } from 'react'
import { ExternalLink, RefreshCw, Trash2, Clock, Eye, Languages, Lock } from 'lucide-react'
import { generateShareUrl } from '../utils/shareUrl'

interface Share {
  id: string
  source_language: string
  target_language: string
  created_at: string
  access_count: number
  code_hash?: string
}

interface ShareStats {
  total_shares: string
  total_accesses: string
  last_share_created: string
  avg_accesses_per_share: string
}

const DEV_KEY = 'slens_dev_betatester_2025'

// Helper function to format relative time (e.g., "2 mins ago")
const getTimeAgo = (dateString: string): string => {
  const now = new Date()
  const past = new Date(dateString)
  const diffMs = now.getTime() - past.getTime()
  const diffSecs = Math.floor(diffMs / 1000)
  const diffMins = Math.floor(diffSecs / 60)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffSecs < 60) return 'just now'
  if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
  return past.toLocaleDateString()
}

const SharesAdmin: React.FC = () => {
  // Check dev key protection
  const urlParams = new URLSearchParams(window.location.search)
  const providedKey = urlParams.get('key')
  const isAuthorized = providedKey === DEV_KEY

  const [shares, setShares] = useState<Share[]>([])
  const [stats, setStats] = useState<ShareStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Use empty string for Vite proxy in dev, full URL in production
  const baseUrl = import.meta.env.VITE_API_BASE_URL || ''
  const frontendUrl = window.location.origin

  const fetchShares = async () => {
    setLoading(true)
    setError(null)

    try {
      const response = await fetch(`${baseUrl}/api/shares/stats`)

      if (!response.ok) {
        throw new Error(`Failed to fetch shares: ${response.statusText}`)
      }

      const data = await response.json()
      setStats(data.statistics)
      setShares(data.recentShares)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load shares')
      console.error('Error fetching shares:', err)
    } finally {
      setLoading(false)
    }
  }

  const deleteShare = async (shareId: string) => {
    if (!confirm('Are you sure you want to delete this share?')) {
      return
    }

    try {
      const response = await fetch(`${baseUrl}/api/shares/${shareId}`, {
        method: 'DELETE'
      })

      if (!response.ok) {
        throw new Error('Failed to delete share')
      }

      // Refresh the list
      await fetchShares()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete share')
      console.error('Error deleting share:', err)
    }
  }

  const getShareUrl = (share: Share) => {
    // Generate semantic URL with language context (e.g., /snippet/ctojs-0fcb7df0)
    return generateShareUrl(share.id, share.source_language, share.target_language, frontendUrl)
  }

  const openShare = (share: Share) => {
    // Save admin URL to localStorage so share viewer can navigate back
    const adminUrl = window.location.href
    localStorage.setItem('syntaxlens_return_url', adminUrl)

    window.open(getShareUrl(share), '_blank')
  }

  const copyShareUrl = async (share: Share) => {
    const url = getShareUrl(share)
    try {
      await navigator.clipboard.writeText(url)
      alert('Share URL copied to clipboard!')
    } catch (err) {
      // Fallback
      const textArea = document.createElement('textarea')
      textArea.value = url
      textArea.style.position = 'fixed'
      textArea.style.opacity = '0'
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand('copy')
      textArea.remove()
      alert('Share URL copied to clipboard!')
    }
  }

  useEffect(() => {
    if (isAuthorized) {
      fetchShares()
    }
  }, [isAuthorized])

  // Access Denied UI
  if (!isAuthorized) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md text-center">
          <Lock className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600 mb-4">
            This page requires a dev key to access.
          </p>
          <p className="text-sm text-gray-500 font-mono">
            Add ?key=YOUR_KEY to the URL
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Shares Admin</h1>
              <p className="text-gray-500 mt-1">Manage all shareable SyntaxLens translations</p>
            </div>
            <button
              onClick={fetchShares}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Statistics Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Languages className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Shares</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.total_shares}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Eye className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Views</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.total_accesses}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Clock className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Avg Views/Share</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {parseFloat(stats.avg_accesses_per_share).toFixed(1)}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Clock className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Last Created</p>
                  <p className="text-sm font-medium text-gray-900">
                    {new Date(stats.last_share_created).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-8">
            <p className="text-red-800 text-sm">❌ {error}</p>
          </div>
        )}

        {/* Shares - Desktop Table + Mobile Cards */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Recent Shares</h2>
              <p className="text-xs text-gray-500 mt-0.5">Up to 50 most recent shares (newest first)</p>
            </div>
            {shares.length > 0 && (
              <div className="text-sm text-gray-500">
                {shares.length} share{shares.length !== 1 ? 's' : ''}
              </div>
            )}
          </div>

          {loading ? (
            <div className="p-12 text-center">
              <RefreshCw className="w-8 h-8 animate-spin text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Loading shares...</p>
            </div>
          ) : shares.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-gray-500">No shares found</p>
            </div>
          ) : (
            <>
              {/* MOBILE VIEW: Card Layout (hidden on desktop) */}
              <div className="md:hidden divide-y divide-gray-200">
                {shares.map((share) => (
                  <div key={share.id} className="p-4 hover:bg-gray-50 transition-colors">
                    {/* Language Pair */}
                    <div className="flex items-center gap-2 mb-3">
                      <Languages className="w-5 h-5 text-gray-400" />
                      <span className="text-lg font-semibold text-gray-900">
                        {share.source_language}
                      </span>
                      <span className="text-gray-400">→</span>
                      <span className="text-lg font-semibold text-gray-900">
                        {share.target_language}
                      </span>
                    </div>

                    {/* Metadata */}
                    <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {getTimeAgo(share.created_at)}
                      </div>
                      <div className="flex items-center gap-1">
                        <Eye className="w-4 h-4" />
                        {share.access_count} views
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => openShare(share)}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium"
                      >
                        <ExternalLink className="w-5 h-5" />
                        Open Share
                      </button>
                      <button
                        onClick={() => copyShareUrl(share)}
                        className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                      >
                        Copy URL
                      </button>
                      <button
                        onClick={() => deleteShare(share.id)}
                        className="px-4 py-3 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                        title="Delete share"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* DESKTOP VIEW: Table Layout (hidden on mobile) */}
              <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Languages
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Share URL
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Views
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {shares.map((share) => (
                    <tr key={share.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <Languages className="w-4 h-4 text-gray-400" />
                          <span className="text-sm font-medium text-gray-900">
                            {share.source_language}
                          </span>
                          <span className="text-gray-400">→</span>
                          <span className="text-sm font-medium text-gray-900">
                            {share.target_language}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => copyShareUrl(share)}
                          className="text-sm font-mono text-blue-600 hover:text-blue-800 hover:underline"
                          title="Click to copy"
                        >
                          {getShareUrl(share).substring(0, 50)}...
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(share.created_at).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <Eye className="w-3 h-3" />
                          {share.access_count}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openShare(share)}
                            className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50 transition-colors"
                            title="Open share"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => deleteShare(share.id)}
                            className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50 transition-colors"
                            title="Delete share"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </>
          )}
        </div>

        {/* Footer Note */}
        <div className="mt-6 text-center text-sm text-gray-500">
          <p>Showing {shares.length} most recent share{shares.length !== 1 ? 's' : ''} (sorted by creation date, newest first)</p>
          {shares.length > 0 && (
            <p className="mt-1 text-xs">
              Can't find a share? Click <strong>Refresh</strong> button above to reload the list.
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

export default SharesAdmin
